"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"
import { HttpTypes } from "@medusajs/types"
import { RadioGroup, Radio as RadioGroupOption } from "@headlessui/react"
import { clx } from "@medusajs/ui"
import { useExpressCart } from "@/modules/express-checkout/providers/express-cart"
import Card from "../card"
import Button from "@/modules/common/components/button"
import Radio from "@/modules/common/components/radio"
import { convertToLocale } from "@/lib/util/money"
import { getExpressPaymentProviders } from "@/lib/data/express-cart"
import { paymentInfoMap, isBankart, getPaymentMethodTitle } from "@/lib/constants"
import Thumbnail from "@/modules/products/components/thumbnail"

type ExpressPaymentProps = {
  countryCode: string
  productHandle: string
  isActive: boolean
}

/**
 * Payment step in express checkout
 * Shows order summary and allows payment method selection
 */
const ExpressPayment = ({
  countryCode,
  productHandle,
  isActive,
}: ExpressPaymentProps) => {
  const router = useRouter()
  const {
    cart,
    initiatePayment,
    completeExpressCart,
    isLoading,
    updateItemQuantity,
  } = useExpressCart()

  const [paymentProviders, setPaymentProviders] = useState<
    HttpTypes.StorePaymentProvider[]
  >([])
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState<string>("")
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [placingOrder, setPlacingOrder] = useState(false)
  const completedStepRef = useRef(false)

  // Fetch payment providers
  useEffect(() => {
    if (!cart?.region_id || !isActive) return

    const fetchPaymentProviders = async () => {
      setLoadingProviders(true)
      try {
        const providers = await getExpressPaymentProviders(cart.region_id!)
        setPaymentProviders(providers || [])

        // Pre-select first provider
        if (providers?.[0]?.id) {
          setSelectedPaymentProvider(providers[0].id)
        }
      } catch (error) {
        console.error("Error fetching payment providers:", error)
        setPaymentProviders([])
      } finally {
        setLoadingProviders(false)
      }
    }

    fetchPaymentProviders()
  }, [cart?.region_id, isActive])

  // Format price helper
  const formatPrice = (amount: number | null | undefined) => {
    return convertToLocale({
      amount: amount || 0,
      currency_code: cart?.currency_code || "EUR",
    })
  }

  // Line item total: use API total when present (requires +items.* expand), else unit_price * quantity
  const getLineItemTotal = (item: HttpTypes.StoreCartLineItem) => {
    const total = item.total
    if (total != null && total > 0) return total
    const unitPrice = item.unit_price ?? 0
    const qty = item.quantity ?? 0
    return unitPrice * qty
  }

  // Handle place order
  const handlePlaceOrder = async () => {
    if (!selectedPaymentProvider || !cart) return

    setPlacingOrder(true)

    try {
      // Card payments use the full checkout embedded form — do not pre-create a
      // Bankart session here (that would open the external hosted page instead).
      if (isBankart(selectedPaymentProvider)) {
        captureAnalyticsEvent("payment_method_selected", {
          payment_method_id: selectedPaymentProvider,
          payment_method_name: getPaymentMethodTitle(selectedPaymentProvider),
        })
        completedStepRef.current = true
        router.push(`/${countryCode}/checkout?cartId=${cart.id}&step=payment`)
        return
      }

      const activeSession = cart.payment_collection?.payment_sessions?.find(
        (session: any) => session.status === "pending"
      )

      if (!activeSession || activeSession.provider_id !== selectedPaymentProvider) {
        await initiatePayment(selectedPaymentProvider)
      }

      captureAnalyticsEvent("checkout_step_completed", {
        step: "payment",
        checkout_type: "express",
      })

      const order = await completeExpressCart()

      if (order) {
        completedStepRef.current = true
        router.push(
          `/${countryCode}/order/confirmed/${order.id}`
        )
      }
    } catch (error) {
      console.error("Error placing order:", error)
      captureAnalyticsEvent("payment_error", {
        payment_method_id: selectedPaymentProvider,
        payment_method_name: getPaymentMethodTitle(selectedPaymentProvider),
        error_code: error instanceof Error ? error.message : "unknown_payment_error",
      })
      captureAnalyticsEvent("order_failed", {
        payment_method_id: selectedPaymentProvider,
        payment_method_name: getPaymentMethodTitle(selectedPaymentProvider),
      })
    } finally {
      setPlacingOrder(false)
    }
  }

  const handlePaymentProviderChange = (paymentProviderId: string) => {
    setSelectedPaymentProvider(paymentProviderId)
    captureAnalyticsEvent("payment_method_selected", {
      payment_method_id: paymentProviderId,
      payment_method_name: getPaymentMethodTitle(paymentProviderId),
    })
  }

  // Handle edit
  const handleEdit = () => {
    router.push(
      `/${countryCode}/products/${productHandle}?express=true&step=payment`
    )
  }

  // Check if we can place order
  const canPlaceOrder = useMemo(() => {
    return (
      !!selectedPaymentProvider &&
      !!cart?.shipping_address &&
      !!cart?.billing_address &&
      !!cart?.shipping_methods?.length &&
      !!cart?.items?.length
    )
  }, [selectedPaymentProvider, cart])

  useEffect(() => {
    if (isActive) {
      completedStepRef.current = false
    }
  }, [isActive])

  useEffect(() => {
    if (!isActive) {
      return
    }

    return () => {
      if (!completedStepRef.current) {
        captureAnalyticsEvent("checkout_step_abandoned", {
          step: "payment",
          checkout_type: "express",
        })
      }
    }
  }, [isActive])

  if (!isActive) return null

  return (
    <Card title="4. Plačilo" isActive={isActive} isCompleted={false}>
      <div className="flex flex-col gap-6">
        {/* Order Summary */}
        <div className="flex flex-col gap-4">
          <h4 className="font-medium text-neutral-900">Povzetek naročila</h4>

          {/* Cart Items */}
          {cart?.items?.map((item) => (
            <div key={item.id} className="flex items-center gap-4">
              <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-neutral-100">
                <Thumbnail
                  thumbnail={item.thumbnail}
                  alt={item.title || "Cart item"}
                  size="square"
                />
              </div>
              <div className="flex-1 flex flex-col">
                <span className="font-medium text-sm">{item.title}</span>
                <span className="text-sm text-neutral-500">
                  Količina:{" "}
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => {
                      const newQty = parseInt(e.target.value) || 0
                      if (newQty > 0) {
                        updateItemQuantity(item.id, newQty)
                      }
                    }}
                    className="w-12 px-2 py-1 border border-neutral-200 rounded text-center"
                    min={1}
                  />
                </span>
              </div>
              <span className="font-medium">{formatPrice(getLineItemTotal(item))}</span>
            </div>
          ))}

          <hr className="border-neutral-200" />

          {/* Totals */}
          <div className="flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-600">Vmesna vsota:</span>
              <span>{formatPrice(cart?.item_subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">Dostava:</span>
              <span>{formatPrice(cart?.shipping_total)}</span>
            </div>
            {(cart?.discount_total ?? 0) > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Popust:</span>
                <span>-{formatPrice(cart?.discount_total)}</span>
              </div>
            )}
            <hr className="border-neutral-200" />
            <div className="flex justify-between font-semibold text-base">
              <span>Skupaj:</span>
              <span>{formatPrice(cart?.total)}</span>
            </div>
          </div>
        </div>

        <hr className="border-neutral-200" />

        {/* Delivery Address Summary */}
        {cart?.shipping_address && (
          <div className="flex flex-col gap-2">
            <h4 className="font-medium text-neutral-900">Naslov za dostavo</h4>
            <div className="text-sm text-neutral-600">
              <p>
                {cart.shipping_address.first_name}{" "}
                {cart.shipping_address.last_name}
              </p>
              <p>{cart.shipping_address.address_1}</p>
              <p>
                {cart.shipping_address.postal_code} {cart.shipping_address.city}
              </p>
            </div>
          </div>
        )}

        <hr className="border-neutral-200" />

        {/* Payment Method Selection */}
        <div className="flex flex-col gap-4">
          <h4 className="font-medium text-neutral-900">Način plačila</h4>

          {loadingProviders ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-neutral-900" />
            </div>
          ) : paymentProviders.length === 0 ? (
            <div className="text-center py-4 text-neutral-500">
              Ni razpoložljivih načinov plačila.
            </div>
          ) : (
            <RadioGroup
              value={selectedPaymentProvider}
              onChange={handlePaymentProviderChange}
            >
              <div className="flex flex-col gap-2">
                {paymentProviders.map((provider) => (
                  <RadioGroupOption
                    key={provider.id}
                    value={provider.id}
                    className={clx(
                      "flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all",
                      {
                        "border-neutral-900 bg-neutral-50":
                          selectedPaymentProvider === provider.id,
                        "border-neutral-200 hover:border-neutral-300":
                          selectedPaymentProvider !== provider.id,
                      }
                    )}
                  >
                    <Radio checked={selectedPaymentProvider === provider.id} />
                    <div className="flex items-center gap-2">
                      {paymentInfoMap[provider.id]?.icon}
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {getPaymentMethodTitle(provider.id)}
                        </span>
                        {paymentInfoMap[provider.id]?.description && (
                          <span className="text-xs text-neutral-500">
                            {paymentInfoMap[provider.id].description}
                          </span>
                        )}
                      </div>
                    </div>
                  </RadioGroupOption>
                ))}
              </div>
            </RadioGroup>
          )}

          {isBankart(selectedPaymentProvider) && (
            <p className="text-sm text-neutral-500 bg-neutral-100 p-3 rounded-lg">
              Za plačilo s kreditno kartico boste preusmerjeni na varno stran za vnos
              podatkov kartice.
            </p>
          )}
        </div>

        <hr className="border-neutral-200" />

        {/* Place Order Button */}
        <Button
          onClick={handlePlaceOrder}
          isLoading={isLoading || placingOrder}
          disabled={!canPlaceOrder}
          className="w-full"
          size="large"
        >
          {isBankart(selectedPaymentProvider)
            ? `Nadaljuj na plačilo ${formatPrice(cart?.total)}`
            : `Plačaj ${formatPrice(cart?.total)}`}
        </Button>
      </div>
    </Card>
  )
}

export default ExpressPayment

